import React, {
  createContext,
  FormHTMLAttributes,
  PropsWithChildren,
  useContext,
  useLayoutEffect,
} from 'react';

export type PasswordManagerAutofillMode = 'allow' | 'ignore';

export interface PasswordManagerIgnoredInputProps {
  'data-lpignore': 'true';
  'data-1p-ignore': 'true';
  'data-bwignore': 'true';
  'data-form-type': 'other';
}

interface PasswordManagerAutofillProviderProps {
  mode: PasswordManagerAutofillMode;
}

const PasswordManagerAutofillContext =
  createContext<PasswordManagerAutofillMode>('allow');

const ignoredInputProps: PasswordManagerIgnoredInputProps = {
  'data-lpignore': 'true',
  'data-1p-ignore': 'true',
  'data-bwignore': 'true',
  'data-form-type': 'other',
};

const allowedInputProps = {};

// Password managers read `autocomplete` from the input itself; an input inside
// <form autocomplete="off"> still reports an empty `input.autocomplete`. Inputs
// in sensitive forms therefore carry the token themselves.
const sensitiveInputProps = {
  ...ignoredInputProps,
  autoComplete: 'off',
} as const;

const SensitiveFormContext = createContext(false);

const LASTPASS_UI_ROOT_SELECTOR =
  '[data-lastpass-icon-root], [data-lastpass-root]';

interface DisplayStyle {
  priority: string;
  value: string;
}

const getLastPassUIRoots = (root: ParentNode): HTMLElement[] => {
  const descendants = Array.from(
    root.querySelectorAll<HTMLElement>(LASTPASS_UI_ROOT_SELECTOR)
  );

  if (root instanceof HTMLElement && root.matches(LASTPASS_UI_ROOT_SELECTOR)) {
    return [root, ...descendants];
  }

  return descendants;
};

export const PasswordManagerAutofillProvider = ({
  children,
  mode,
}: PropsWithChildren<PasswordManagerAutofillProviderProps>) => {
  useLayoutEffect(() => {
    if (mode !== 'ignore') {
      return undefined;
    }

    const originalDisplayStyles = new Map<HTMLElement, DisplayStyle>();

    const hideLastPassUIRoots = (root: ParentNode) => {
      getLastPassUIRoots(root).forEach(element => {
        if (!originalDisplayStyles.has(element)) {
          originalDisplayStyles.set(element, {
            priority: element.style.getPropertyPriority('display'),
            value: element.style.getPropertyValue('display'),
          });
        }

        element.style.setProperty('display', 'none', 'important');
      });
    };

    hideLastPassUIRoots(document);

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node instanceof HTMLElement) {
            hideLastPassUIRoots(node);
          }
        });
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();

      originalDisplayStyles.forEach(({ priority, value }, element) => {
        if (value) {
          element.style.setProperty('display', value, priority);
        } else {
          element.style.removeProperty('display');
        }
      });
    };
  }, [mode]);

  return (
    <PasswordManagerAutofillContext.Provider value={mode}>
      {children}
    </PasswordManagerAutofillContext.Provider>
  );
};

/**
 * Form boundary for credential- and identity-bearing CMS forms. Browser
 * autocomplete is disabled on the form and on every shared input inside it;
 * an input's own `autoComplete` prop (e.g. `new-password`) still wins.
 */
export const SensitiveForm = ({
  children,
  ...formProps
}: PropsWithChildren<FormHTMLAttributes<HTMLFormElement>>) => (
  <SensitiveFormContext.Provider value>
    <form {...formProps} autoComplete="off">
      {children}
    </form>
  </SensitiveFormContext.Provider>
);

export const usePasswordManagerInputProps = () => {
  const mode = useContext(PasswordManagerAutofillContext);
  const isSensitiveForm = useContext(SensitiveFormContext);

  if (isSensitiveForm) {
    return sensitiveInputProps;
  }

  return mode === 'ignore' ? ignoredInputProps : allowedInputProps;
};
